/**
 * SupplierInbox — Buyer view (admin/editor/auditor) of every incoming
 * supplier submission. S2 renders + filters; approve/reject hooks are
 * stubbed until S3 wires the review endpoints.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, RefreshCw, Inbox } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import api from '@/lib/api';

const STATUS_OPTIONS = ['all', 'submitted', 'under_review', 'approved', 'rejected'];

export default function SupplierInbox() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/suppliers/submissions?limit=200');
      setRows(r.data?.submissions || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load submissions');
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(
    () => filter === 'all' ? rows : rows.filter((r) => r.status === filter),
    [rows, filter],
  );

  const notYet = () => toast.info('Approve / reject ships in Phase S3');

  const decide = async (sub, decision) => {
    let reason = '';
    if (decision === 'reject') {
      reason = window.prompt('Reason for rejection?') || '';
      if (!reason.trim()) { toast.error('A reason is required to reject'); return; }
    }
    try {
      await api.post(`/suppliers/submissions/${sub.id}/${decision}`, { reason });
      toast.success(`Submission ${decision === 'approve' ? 'approved' : 'rejected'}`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || `${decision} failed`);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-4" data-testid="supplier-inbox">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-semibold flex items-center gap-2">
            <Inbox className="w-6 h-6" /> Supplier inbox
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Incoming data submissions from your suppliers.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select data-testid="inbox-filter-select"
                  value={filter} onChange={(e) => setFilter(e.target.value)}
                  className="flex h-9 rounded-md border border-input bg-background px-3 text-sm">
            {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <Button variant="outline" size="sm" onClick={load} data-testid="inbox-refresh-btn">
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </div>

      <Card>
        {loading ? (
          <div className="p-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-sm text-muted-foreground" data-testid="inbox-empty">
            No submissions{filter !== 'all' ? ` with status "${filter}"` : ''}.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="text-left px-4 py-2.5">Supplier</th>
                  <th className="text-left px-4 py-2.5">Product</th>
                  <th className="text-left px-4 py-2.5">Type</th>
                  <th className="text-left px-4 py-2.5">Status</th>
                  <th className="text-left px-4 py-2.5">Submitted</th>
                  <th className="text-right px-4 py-2.5">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filtered.map((s) => (
                  <tr key={s.id} className="hover:bg-muted/30 transition-colors" data-testid={`inbox-row-${s.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium truncate max-w-[220px]" title={s.submitted_email}>
                        {s.submitted_email || s.supplier_id?.slice(0, 8)}
                      </div>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">{String(s.product_id).slice(0, 12)}…</td>
                    <td className="px-4 py-3">
                      <span className="text-xs uppercase tracking-wide">{s.submission_type}</span>
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={s.status} />
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">
                      {s.submitted_at ? new Date(s.submitted_at).toLocaleString() : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1">
                        {s.status === 'submitted' ? (
                          <>
                            <Button size="sm" variant="outline" onClick={() => decide(s, 'approve')} data-testid={`inbox-approve-${s.id}`}>Approve</Button>
                            <Button size="sm" variant="outline" onClick={() => decide(s, 'reject')} data-testid={`inbox-reject-${s.id}`}>Reject</Button>
                          </>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    submitted: 'bg-amber-500/10 text-amber-600 border-amber-500/30',
    under_review: 'bg-blue-500/10 text-blue-600 border-blue-500/30',
    approved: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30',
    rejected: 'bg-red-500/10 text-red-600 border-red-500/30',
  };
  return (
    <Badge variant="outline" className={`${map[status] || 'bg-muted text-muted-foreground border-border'} text-[10px] uppercase tracking-wide`}>
      {status}
    </Badge>
  );
}
